import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import Layout from '../../layout/Layout';
import { getHeaders } from '../../utility/getHeader';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `THB ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function TicketReconciliationReport() {
  const [dateRange, setDateRange] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState({
    summary: null,
    daily: [],
    table_status: { has_tracking_table: true, has_scan_logs_table: true },
  });

  const fetchReport = useCallback(async (rangeValue) => {
    const [start, end] = Array.isArray(rangeValue) ? rangeValue : [];
    const hasDateRange = Boolean(start && end && dayjs.isDayjs(start) && dayjs.isDayjs(end));

    setLoading(true);
    setError('');

    try {
      const requestConfig = {
        ...getHeaders(),
        ...(hasDateRange
          ? {
              params: {
                start_date: start.format('YYYY-MM-DD'),
                end_date: end.format('YYYY-MM-DD'),
              },
            }
          : {}),
      };

      const response = await axios.get('/reports/ticket-reconciliation', requestConfig);

      if (response.data?.success) {
        const serverFilters = response.data.filters || {};
        if (!hasDateRange && serverFilters.start_date && serverFilters.end_date) {
          setDateRange([dayjs(serverFilters.start_date), dayjs(serverFilters.end_date)]);
        }

        setReportData({
          summary: response.data.summary || null,
          daily: response.data.daily || [],
          table_status: response.data.table_status || {
            has_tracking_table: true,
            has_scan_logs_table: true,
          },
        });
      } else {
        setReportData({
          summary: null,
          daily: [],
          table_status: { has_tracking_table: true, has_scan_logs_table: true },
        });
        setError(response.data?.message || 'Failed to load reconciliation report');
      }
    } catch (err) {
      setReportData({
        summary: null,
        daily: [],
        table_status: { has_tracking_table: true, has_scan_logs_table: true },
      });
      setError(err?.response?.data?.message || 'Failed to load reconciliation report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(null);
  }, [fetchReport]);

  const summary = reportData.summary || {
    billed_amount: 0,
    scanned_amount: 0,
    amount_difference: 0,
    generated_codes: 0,
    unique_scanned_codes: 0,
    codes_difference: 0,
    not_found_scans: 0,
    duplicate_scans: 0,
    verified_scans: 0,
    scan_attempts: 0,
    is_amount_matched: false,
    is_codes_matched: false,
  };

  const columns = useMemo(() => [
    {
      title: 'Date',
      dataIndex: 'report_date',
      key: 'report_date',
      width: 120,
    },
    {
      title: 'Bills',
      dataIndex: 'bills_count',
      key: 'bills_count',
      align: 'right',
      width: 90,
    },
    {
      title: 'Billed Amount',
      dataIndex: 'billed_amount',
      key: 'billed_amount',
      align: 'right',
      render: (value) => formatCurrency(value),
      width: 150,
    },
    {
      title: 'Generated Codes',
      dataIndex: 'generated_codes',
      key: 'generated_codes',
      align: 'right',
      width: 140,
    },
    {
      title: 'Unique Scanned',
      dataIndex: 'unique_scanned_codes',
      key: 'unique_scanned_codes',
      align: 'right',
      width: 140,
    },
    {
      title: 'Scanned Amount',
      dataIndex: 'scanned_amount',
      key: 'scanned_amount',
      align: 'right',
      render: (value) => formatCurrency(value),
      width: 150,
    },
    {
      title: 'Amount Diff',
      dataIndex: 'amount_difference',
      key: 'amount_difference',
      align: 'right',
      render: (value) => {
        const color = Number(value) === 0 ? 'success' : Number(value) > 0 ? 'warning' : 'error';
        return <Tag color={color}>{formatCurrency(value)}</Tag>;
      },
      width: 140,
    },
    {
      title: 'Verified',
      dataIndex: 'verified_scans',
      key: 'verified_scans',
      align: 'right',
      width: 90,
    },
    {
      title: 'Duplicate',
      dataIndex: 'duplicate_scans',
      key: 'duplicate_scans',
      align: 'right',
      width: 95,
    },
    {
      title: 'Not Found',
      dataIndex: 'not_found_scans',
      key: 'not_found_scans',
      align: 'right',
      width: 95,
    },
    {
      title: 'Status',
      key: 'status',
      fixed: 'right',
      width: 140,
      render: (_, row) => {
        if (row.is_amount_matched && row.is_codes_matched) {
          return <Tag color="success">Matched</Tag>;
        }
        if (!row.is_amount_matched && !row.is_codes_matched) {
          return <Tag color="error">Mismatch</Tag>;
        }
        return <Tag color="warning">Partial</Tag>;
      },
    },
  ], []);

  const hasTableWarning = !reportData.table_status?.has_tracking_table || !reportData.table_status?.has_scan_logs_table;

  return (
    <Layout>
      <div className="container-fluid mt-3 mb-4">
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Card>
            <Row justify="space-between" align="middle" gutter={[16, 16]}>
              <Col>
                <Title level={4} style={{ margin: 0 }}>Ticket Reconciliation Report</Title>
                <Text type="secondary">Compare bills generated with gate scanner verification totals</Text>
              </Col>
              <Col>
                <Space wrap>
                  <RangePicker
                    value={dateRange}
                    onChange={(value) => setDateRange(value || [])}
                    allowClear={false}
                  />
                  <Button type="primary" onClick={() => fetchReport(dateRange)} loading={loading}>
                    Load Report
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {hasTableWarning && (
            <Alert
              type="warning"
              showIcon
              message="Gate tracking tables are missing"
              description="Please create ticket_barcode_tracking and gate_ticket_scan_logs tables to get full reconciliation results."
            />
          )}

          {error && (
            <Alert
              type="error"
              showIcon
              message="Unable to load reconciliation data"
              description={error}
            />
          )}

          <Spin spinning={loading}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic title="Billed Amount" value={summary.billed_amount} formatter={(v) => formatCurrency(v)} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic title="Scanned Amount" value={summary.scanned_amount} formatter={(v) => formatCurrency(v)} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Amount Difference"
                    value={summary.amount_difference}
                    valueStyle={{ color: Number(summary.amount_difference) === 0 ? '#3f8600' : '#cf1322' }}
                    formatter={(v) => formatCurrency(v)}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Codes Difference"
                    value={summary.codes_difference}
                    valueStyle={{ color: Number(summary.codes_difference) === 0 ? '#3f8600' : '#cf1322' }}
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic title="Generated Codes" value={summary.generated_codes} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic title="Unique Scanned Codes" value={summary.unique_scanned_codes} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic title="Verified Scans" value={summary.verified_scans} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic title="Not Found Scans" value={summary.not_found_scans} />
                </Card>
              </Col>
            </Row>

            <Card title="Daily Reconciliation">
              {reportData.daily.length === 0 ? (
                <Empty description="No reconciliation records found for selected date range" />
              ) : (
                <Table
                  rowKey={(row) => row.report_date}
                  columns={columns}
                  dataSource={reportData.daily}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 1300 }}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right"><strong>{summary.bills_count || 0}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right"><strong>{formatCurrency(summary.billed_amount)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right"><strong>{summary.generated_codes || 0}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right"><strong>{summary.unique_scanned_codes || 0}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right"><strong>{formatCurrency(summary.scanned_amount)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right"><strong>{formatCurrency(summary.amount_difference)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="right"><strong>{summary.verified_scans || 0}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="right"><strong>{summary.duplicate_scans || 0}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={9} align="right"><strong>{summary.not_found_scans || 0}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={10}>
                        {summary.is_amount_matched && summary.is_codes_matched ? (
                          <Tag color="success">Matched</Tag>
                        ) : (
                          <Tag color="error">Mismatch</Tag>
                        )}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              )}
            </Card>
          </Spin>
        </Space>
      </div>
    </Layout>
  );
}
